import { UntypedFormControl } from '@angular/forms'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core'
import { SearchModel } from '@flocasts/experience-service-types'
import { Subject } from 'rxjs'
import { debounceTime, map, takeUntil, filter } from 'rxjs/operators'
import { SegmentEvents } from '../analytics/models/segment-events.model'
import { SegmentService } from '../analytics/services/segment.service'
import { getCleanSearchTerm } from './search-input.utility'
import { notNullOrUndefined } from '../functions/type-guards'

@Component({
  selector: 'flo-search-input-next-gen',
  styleUrls: ['./search-input.component.scss'],
  templateUrl: './search-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchInputNextGenComponent implements OnInit, OnDestroy, OnChanges {
  constructor(private readonly segmentService: SegmentService) {}

  /**
   * Pre-populated search term from query params given by the parent component
   * @deprecated This property will be removed when all components using next-gen-search
   * provide a SearchModel from the BFF.
   */
  @Input() searchTerm = ''

  /**
   * The search model that all components using next-gen-search should provide
   */
  @Input() searchModel: SearchModel

  /**
   * Placeholder to show when a user clicks into the input
   * @deprecated This property will be removed when all components using next-gen-search
   * provide a SearchModel from the BFF
   */
  @Input() placeholder = 'Search'

  /**
   * Stretchy adds the stretchy class to the input element
   * This makes the input field expand when the user clicks into the input
   */
  @Input() stretchy = false

  /**
   * Debounce time between a new user keystroke and the emit output and trackEvent call
   */
  @Input() debounce = 750

  /**
   * User's search query from the input field.
   * Emits the string of the search, or null when it is empty or the user hits clear.
   */
  @Output() search = new EventEmitter<{ searchTerm: string | null; searchModel?: SearchModel | null }>()

  searchFormControl = new UntypedFormControl()
  private readonly ngOnDestroy$ = new Subject<void>()

  /**
   * debounceTime can only be set once, so we handle this in a lifecycle event to wait for the debounce input to be completely set
   */
  ngOnInit(): void {
    this.searchFormControl.valueChanges
      .pipe(
        filter(notNullOrUndefined),
        debounceTime(this.debounce),
        map(getCleanSearchTerm),
        takeUntil(this.ngOnDestroy$)
      )
      .subscribe(searchTerm => {
        this.search.emit({ searchTerm, searchModel: this.searchModel })
        this.trackSearchQuery(searchTerm)
      })
  }

  trackSearchQuery(query?: string | null): void {
    if (!query) return
    this.segmentService.trackEvent({
      name: SegmentEvents.SEARCH_QUERY_MADE,
      properties: {
        search_term: query,
        name: this.searchModel?.placeholderText ?? this.placeholder
      }
    })
  }

  readonly clearSearch = (input: HTMLInputElement) => {
    // refocus on the input, so that we don't shrink the element when stretchy is activated
    input.focus()
    this.searchFormControl.reset()
    // We don't want to wait for debouncer to clear the search.
    this.search.emit({ searchTerm: null, searchModel: this.searchModel })
    this.segmentService.trackEvent({
      name: SegmentEvents.SEARCH_CLEARED,
      properties: {
        search_term: '',
        name: this.searchModel?.placeholderText ?? this.placeholder
      }
    })
  }

  ngOnChanges() {
    if (this.searchFormControl.value !== this.searchModel?.text) {
      this.searchFormControl.setValue(this.searchModel?.text || this.searchTerm)
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }
}
